import React, { useState, useLayoutEffect, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { toggleModalDiagramShare, diagramShare } from '../../../redux/main/main.actions';
import { selectIsModalDiagramShareEnabled, selectModalDiagramShareData } from '../../../redux/main/main.selectors';

import {
    fetchDiagramMembers,
    changeRoleForDiagramMember,
    removeDiagramMember,
    fetchDiagramInvites,
    createDiagramInvite,
    revokeDiagramInvite,
} from '../../../redux/canvas/canvas.actions';
import { selectDiagramMembers, selectDiagramInvites } from '../../../redux/canvas/canvas.selectors';

import { showNotify } from '../../../redux/notify/notify.actions';

import Modal from '../Modal/Modal';

import './ModalDiagramShare.scss';
import ButtonDropdown from '../../../pages/MainApp/_components/ButtonDropdown/ButtonDropdown';
import ButtonDropdownInvite from '../../../pages/MainApp/_components/ButtonDropdown/ButtonDropdownInvite';

function buildViewMode(accessRole = 'view') {
    switch (accessRole) {
        case 'view':
        case 'comment':
            return 'type_1';
        case 'edit':
            return 'type_2';
        case 'editadmin':
        case 'own':
            return 'type_3';
        default:
            return 'type_1';
    }
}

function canChangeRole(yourRole, oldMemberRole, newMemberRole) {
    return (
        (yourRole === 'own' && oldMemberRole !== 'own' && newMemberRole !== 'own')
        || (yourRole === 'editadmin' && !['own', 'editadmin'].includes(oldMemberRole) && !['own', 'editadmin'].includes(newMemberRole))
    );
}

export const ModalDiagramShare = () => {
    const dispatch = useDispatch();

    const isOpen = useSelector(selectIsModalDiagramShareEnabled);
    const modalData = useSelector(selectModalDiagramShareData);

    const diagramMembers = useSelector(selectDiagramMembers);
    const diagramInvites = useSelector(selectDiagramInvites);
    // const diagramInvites = [
    // {
    //     id: 123,
    //     role: 'view',
    //     email: 'test@email.com',
    // },
    // ];

    const diagramId = '123123123123'; // select
    const yourRole = 'own'; // accessRole // select
    const yourId = 123; // select

    const [selectedRole, setSelectedRole] = useState('view');
    const [inviteValue, setInviteValue] = useState('');
    const [isBtnInviteClicked, setIsBtnInviteClicked] = useState(false);

    const inputRef = useRef(null);
    const [viewType, setViewType] = useState('type_1');
    const [isLinkCopied, setIsLinkCopied] = useState(false);

    useEffect(() => {
        if (!modalData?.diagram?.id) return;

        dispatch(fetchDiagramMembers(modalData?.diagram?.id));
        setViewType(buildViewMode(yourRole));

        if (yourRole === 'own') {
            dispatch(fetchDiagramInvites(modalData?.diagram?.id));
        }

        return () => { };
    }, [modalData]);

    const handleCancel = () => {
        dispatch(toggleModalDiagramShare(null));
        setTimeout(() => {
            setInviteValue('');
            setIsLinkCopied(false);

            setSelectedRole('view');
        }, 300);
    };

    const handleSave = () => {
        // dispatch(diagramShare(modalData?.diagram?.id, inviteValue));
        dispatch(toggleModalDiagramShare(null));

        setTimeout(() => {
            setInviteValue('');
            setIsLinkCopied(false);

            setSelectedRole('view');
        }, 300);
    };

    // const handleKeyDown = (e) => {
    //     if (e.key === 'Enter') {
    //         e.preventDefault();
    //         handleSave();
    //     }
    // };

    const handleRender = () => {
        if (isOpen && inputRef.current) {
            inputRef.current.focus();
        }
    };

    const handleCopyDiagramLinkClick = async (e) => {
        const baseUrl = window.location.origin;
        const path = `/diagram/${modalData?.diagram?.id}`;
        const fullUrl = `${baseUrl}${path}`;

        try {
            await navigator.clipboard.writeText(fullUrl);
            dispatch(showNotify({ message: 'The link has been copied' }));
        } catch (err) { }

        if (!isLinkCopied) {
            setIsLinkCopied(true);
        }
    };

    const handleBtnInviteClick = () => {
        if (!isBtnInviteClicked) {
            dispatch(createDiagramInvite(diagramId, inviteValue, selectedRole));
            setIsBtnInviteClicked(true);

            setTimeout(() => {
                setInviteValue('');
                // setSelectedRole('view');
                setIsBtnInviteClicked(false);
            }, 2000);
        }
    };

    const handleChangeMemberRole = (memberId, newRole) => {
        const member = diagramMembers.find(member => member.id == memberId);
        if (member && member.role !== newRole) {
            const canChangeMemberRole = canChangeRole(yourRole, member.role, newRole);

            if (canChangeMemberRole) {
                dispatch(changeRoleForDiagramMember(diagramId, memberId, newRole));
            }
        }
    };

    const handleRemoveMember = (memberId) => {
        const member = diagramMembers.find(member => member.id == memberId);
        if (member) {
            const canChangeMemberRole = canChangeRole(yourRole, member.role, member.role);
            if (canChangeMemberRole) {
                dispatch(removeDiagramMember(diagramId, memberId));
            }
        }
    };

    const handleRevokeInvite = (inviteId) => {
        const invite = diagramInvites.find(invite => invite.id == inviteId);
        if (invite) {
            dispatch(revokeDiagramInvite(diagramId, inviteId));
        }
    };


    // own => ['view', 'comment', 'edit', 'editadmin'] + can remove members
    const menuItemsOwn = [
        // TODO: editadmin
        // {
        //     role: 'editadmin',
        //     onClick: (memberId, newRole) => { handleChangeMemberRole(memberId, newRole); },
        // },
        {
            role: 'edit',
            onClick: (memberId, newRole) => { handleChangeMemberRole(memberId, newRole); },
        },
        {
            role: 'comment',
            onClick: (memberId, newRole) => { handleChangeMemberRole(memberId, newRole); },
        },
        {
            role: 'view',
            onClick: (memberId, newRole) => { handleChangeMemberRole(memberId, newRole); },
        },
        {
            line: true,
        },
        {
            role: 'remove',
            onClick: (memberId) => { handleRemoveMember(memberId); },
        },
    ];

    // editadmin => ['view', 'comment', 'edit'] + can not to remove members
    const menuItemsEdit = [
        {
            role: 'edit',
            onClick: (memberId, newRole) => { handleChangeMemberRole(memberId, newRole); },
        },
        {
            role: 'comment',
            onClick: (memberId, newRole) => { handleChangeMemberRole(memberId, newRole); },
        },
        {
            role: 'view',
            onClick: (memberId, newRole) => { handleChangeMemberRole(memberId, newRole); },
        },
    ];

    const menuItemsInvited = [
        {
            role: 'revoke',
            onClick: (inviteId) => { handleRevokeInvite(inviteId); },
        },
    ];

    return (
        <Modal
            isOpen={isOpen}
            // modalClass={`modal-share-invite-people ${modalData?.page === 'main' ? 'modal-share-main' : ''} ${viewType}`}
            modalClass={`modal-share-invite-people ${viewType}`}
            onCancel={handleCancel}
            onShouldRender={handleRender}
        >
            <div className="header">Share diagram</div>
            <div className="form-group one">
                {
                    ['type_1', 'type_2'].includes(viewType)
                        // ? <div className='info-text'>Only the diagram owner and admins can invite new members.</div>
                        ? <div className='info-text'>Only the diagram owner can invite new members.</div>
                        : <React.Fragment>
                            <InviteInput isModalOpen={isOpen} selectedRole={selectedRole} setSelectedRole={setSelectedRole} value={inviteValue} setValue={setInviteValue} yourRole={yourRole} />
                            <button
                                disabled={isBtnInviteClicked}
                                className={`invite-button ${isBtnInviteClicked ? 'invited' : ''}`}
                                onClick={(e) => handleBtnInviteClick(e)}
                            >
                                {!isBtnInviteClicked ?
                                    <div>Invite</div>
                                    : <React.Fragment>
                                        <svg width="21px" height="26px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g><path d="M4 12L8.94975 16.9497L19.5572 6.34326" stroke="#29d1ac" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path></g></svg>
                                        <div>Invited</div>
                                    </React.Fragment>
                                }
                            </button>
                        </React.Fragment>
                }
            </div>

            {['type_2', 'type_3'].includes(viewType)
                ? <div className="members-group members-first">
                    {diagramMembers?.map((member, index) =>
                        <div key={`mem-item-${index}`} className='members-item'>
                            <div className="avatar">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                            </div>
                            <div className="dash-sidebar-profile-description">
                                <span className="profile-name">{member.name} {member.id === yourId ? <span className="you">(you)</span> : ''}</span>
                                <span className="profile-email">{member.title}</span>
                            </div>
                            {
                                (yourRole === 'editadmin' && ['edit', 'comment', 'view'].includes(member.role) ||
                                    yourRole === 'own' && ['editadmin', 'edit', 'comment', 'view'].includes(member.role))
                                    ? <ButtonDropdown memberId={member.id} memberRole={member.role} menuItems={yourRole === 'own' ? menuItemsOwn : menuItemsEdit} />
                                    : <button tabIndex={-1} className={`dash-sidebar-logout ${member.role}`}>{buildRoleName(member.role)}</button>
                            }
                        </div>
                    )}
                </div>
                : ''
            }
            {['type_3'].includes(viewType)
                ? <div className="members-group">
                    {diagramInvites?.map((invite, index) =>
                        <div key={`mem-item-${index}`} className='members-item'>
                            <div className="avatar">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                            </div>
                            <div className="dash-sidebar-profile-description">
                                <span className="profile-name">{invite.email}</span>
                                <span className="profile-email">Pending <span className="invite-role">({buildRoleName(invite.role)})</span></span>
                            </div>
                            {
                                <ButtonDropdownInvite inviteId={invite.id} menuItems={menuItemsInvited} />
                            }
                        </div>
                    )}
                </div>
                : ''
            }

            <div className='bottom-section'>
                <div className='bottom-section-left'>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 11V8C17 5.23858 14.7614 3 12 3C9.23858 3 7 5.23858 7 8V11M7.8 21H16.2C17.8802 21 18.7202 21 19.362 20.673C19.9265 20.3854 20.3854 19.9265 20.673 19.362C21 18.7202 21 17.8802 21 16.2V15.8C21 14.1198 21 13.2798 20.673 12.638C20.3854 12.0735 19.9265 11.6146 19.362 11.327C18.7202 11 17.8802 11 16.2 11H7.8C6.11984 11 5.27976 11 4.63803 11.327C4.07354 11.6146 3.6146 12.0735 3.32698 12.638C3 13.2798 3 14.1198 3 15.8V16.2C3 17.8802 3 18.7202 3.32698 19.362C3.6146 19.9265 4.07354 20.3854 4.63803 20.673C5.27976 21 6.11984 21 7.8 21Z"></path></svg>
                    <div className='bottom-section-left-text'>
                        <span className='bottom-section-left-text-title'>Protected</span>
                        <span className='bottom-section-left-text-subtitle'>Only users with access can open link</span>
                    </div>
                </div>
                <div className='bottom-section-right'>
                    {!isLinkCopied ?
                        <button className="dash-sidebar-logout link" onClick={(e) => handleCopyDiagramLinkClick(e)}>
                            <svg width="27px" height="27px" viewBox="0 0 24 24" fill="none" transform="rotate(-45)"><g><path d="M10 16H7C4.79086 16 3 14.2091 3 12V12C3 9.79086 4.79086 8 7 8H10" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path><path d="M16 12H8" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path><path d="M14 16H17C19.2091 16 21 14.2091 21 12V12C21 9.79086 19.2091 8 17 8H14" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"></path></g></svg>
                            Copy Link
                        </button>
                        : <button className="dash-sidebar-logout link copied" onClick={(e) => handleCopyDiagramLinkClick(e)}>
                            <svg width="64px" height="64px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g><path d="M4 12L8.94975 16.9497L19.5572 6.34326" stroke="#29d1ac" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path></g></svg>
                            Copied
                        </button>
                    }
                </div>
            </div>
        </Modal>
    );
};

export default ModalDiagramShare;

const ROLES_IDS = ['view', 'comment', 'edit', 'editadmin', 'own'];
const ROLES_MAP = {
    view: 'Viewer',
    comment: 'Commenter',
    edit: 'Editor',
    editadmin: 'Editor + Admin',
    own: 'Owner',
}

function buildRoleName(role) {
    if (ROLES_IDS.includes(role)) {
        return ROLES_MAP[role];
    } else {
        return ROLES_MAP['view'];
    }
}

const InviteInput = ({ value, setValue, selectedRole, setSelectedRole, isModalOpen = false, yourRole = 'editadmin' }) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [menuItems, setMenuItems] = useState([]);

    const inputRef = useRef(null);
    const btnMenuRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (btnMenuRef.current && !btnMenuRef.current.contains(e.target)
                && menuRef.current && !menuRef.current.contains(e.target)) {
                setIsMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleMenuItemClick = (role) => {
        setSelectedRole(role);
        setIsMenuOpen(false);
    };

    const handleBtnMenu = () => {
        setIsMenuOpen(prevValue => !prevValue);
        // if (!isMenuOpen && inputRef.current) {
        //     inputRef.current.focus();
        // }
    };

    useEffect(() => {
        if (isModalOpen && !isMenuOpen && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isMenuOpen]);

    useEffect(() => {
        if (isModalOpen && yourRole) {
            let values = [];

            if (yourRole === 'own') {
                // TODO: editadmin
                // values = ['editadmin', 'edit', 'comment', 'view'];
                values = ['edit', 'comment', 'view'];
            } else if (yourRole === 'editadmin') {
                values = ['edit', 'comment', 'view'];
            }
            setMenuItems(values);
        }
    }, [yourRole]);

    return (
        <div className='input-with-role'>
            <input
                maxLength="150"
                ref={inputRef}
                autoFocus={(window.innerWidth >= 1000)}
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck="false"
                inputMode="email"
                name="email"
                type="email"
                placeholder="Enter email address"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                // onKeyDown={handleKeyDown}
                // className={`input-with-role-input ${!isMenuOpen ? 'focused' : ''}`}
                className={`input-with-role-input`}
            ></input>
            <button
                ref={btnMenuRef}
                className={`input-with-role-button ${isMenuOpen ? 'opened' : ''}`}
                type="button"
                onClick={(e) => handleBtnMenu(e)}
            >
                {buildRoleName(selectedRole)}
            </button>
            {isMenuOpen && (
                // TODO: editadmin
                // <ul className={`input-with-role_menu ${yourRole === 'own' ? 'large' : ''}`}
                <ul className={`input-with-role_menu`}
                    ref={menuRef}>
                    {menuItems.map((item, index) =>
                        <li key={index} className="input-with-role-item">
                            <button
                                type="button"
                                className={`input-with-role-item-btn ${item === selectedRole ? 'selected' : ''}`}
                                onClick={() => handleMenuItemClick(item)}
                            >
                                {item === 'editadmin' ? <svg className="editadmin" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round"><path d="M12 15.5H7.5C6.10444 15.5 5.40665 15.5 4.83886 15.6722C3.56045 16.06 2.56004 17.0605 2.17224 18.3389C2 18.9067 2 19.6044 2 21M19 21V15M16 18H22M14.5 7.5C14.5 9.98528 12.4853 12 10 12C7.51472 12 5.5 9.98528 5.5 7.5C5.5 5.01472 7.51472 3 10 3C12.4853 3 14.5 5.01472 14.5 7.5Z"></path></svg> : ''}
                                {item === 'edit' ? <svg className="edit" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H6.8c-1.68 0-2.52 0-3.162.327a3 3 0 0 0-1.311 1.311C2 6.28 2 7.12 2 8.8v8.4c0 1.68 0 2.52.327 3.162a3 3 0 0 0 1.311 1.311C4.28 22 5.12 22 6.8 22h8.4c1.68 0 2.52 0 3.162-.327a3 3 0 0 0 1.311-1.311C20 19.72 20 18.88 20 17.2V13M8 16h1.675c.489 0 .733 0 .963-.055.204-.05.4-.13.579-.24.201-.123.374-.296.72-.642L21.5 5.5a2.121 2.121 0 0 0-3-3l-9.563 9.563c-.346.346-.519.519-.642.72a2 2 0 0 0-.24.579c-.055.23-.055.474-.055.963z"></path></svg> : ''}
                                {item === 'comment' ? <svg className="comment" width="64px" height="64px" viewBox="0 -0.5 25 25" fill="none" xmlns="http://www.w3.org/2000/svg"><g> <path fillRule="evenodd" clipRule="evenodd" d="M5.5 12.9543C5.51239 14.0398 5.95555 15.076 6.73197 15.8348C7.50838 16.5936 8.55445 17.0128 9.64 17.0003H10.2L11.86 18.7323C12.0291 18.9036 12.2598 19 12.5005 19C12.7412 19 12.9719 18.9036 13.141 18.7323L14.8 17.0003H15.36C16.4456 17.0128 17.4916 16.5936 18.268 15.8348C19.0444 15.076 19.4876 14.0398 19.5 12.9543V8.04428C19.4731 5.7845 17.6198 3.97417 15.36 4.00028H9.64C7.38021 3.97417 5.5269 5.7845 5.5 8.04428V12.9543Z" stroke="currentColor" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"></path> <path d="M9.5 8.25024C9.08579 8.25024 8.75 8.58603 8.75 9.00024C8.75 9.41446 9.08579 9.75024 9.5 9.75024V8.25024ZM15.5 9.75024C15.9142 9.75024 16.25 9.41446 16.25 9.00024C16.25 8.58603 15.9142 8.25024 15.5 8.25024V9.75024ZM9.5 11.2502C9.08579 11.2502 8.75 11.586 8.75 12.0002C8.75 12.4145 9.08579 12.7502 9.5 12.7502V11.2502ZM15.5 12.7502C15.9142 12.7502 16.25 12.4145 16.25 12.0002C16.25 11.586 15.9142 11.2502 15.5 11.2502V12.7502ZM9.5 9.75024H15.5V8.25024H9.5V9.75024ZM9.5 12.7502H15.5V11.2502H9.5V12.7502Z" fill="currentColor"></path> </g></svg> : ''}
                                {item === 'view' ? <svg className="view" width="64px" height="64px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g> <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.5"></circle> <path d="M20.188 10.9343C20.5762 11.4056 20.7703 11.6412 20.7703 12C20.7703 12.3588 20.5762 12.5944 20.188 13.0657C18.7679 14.7899 15.6357 18 12 18C8.36427 18 5.23206 14.7899 3.81197 13.0657C3.42381 12.5944 3.22973 12.3588 3.22973 12C3.22973 11.6412 3.42381 11.4056 3.81197 10.9343C5.23206 9.21014 8.36427 6 12 6C15.6357 6 18.7679 9.21014 20.188 10.9343Z" stroke="currentColor" strokeWidth="1.5"></path> </g></svg> : ''}
                                {buildRoleName(item)}
                            </button>
                        </li>
                    )}
                </ul>
            )}
        </div>
    )
};
